import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex flex-col items-stretch justify-start h-screen w-full min-w-[760px]" }
const _hoisted_2 = { class: "col-span-1 flex flex-row max-w-1/6 h-full items-end justify-end align-center gap-1.5" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("p", { class: "mt-2" }, "Tem certeza de que deseja resetar todos os seus fluxos e passos?"),
  /*#__PURE__*/_createElementVNode("p", { class: "mt-2" }, [
    /*#__PURE__*/_createTextVNode("Todos seus fluxos voltarão ao padrão da aplicação e qualquer mudança será "),
    /*#__PURE__*/_createElementVNode("strong", null, "perdida"),
    /*#__PURE__*/_createTextVNode(". ")
  ])
], -1)
const _hoisted_4 = { class: "line-clamp-1" }
const _hoisted_5 = { class: "line-clamp-1" }
const _hoisted_6 = { class: "flex-1 flex max-h-full overflow-hidden" }

import HeaderComponent from '@/components/HeaderComponent.vue';
import { GET_CREDENTIALS, GET_PARTNER_ID, PARTNER_ID } from '@/store';
import { computed, onMounted, ref, watch } from 'vue';

import {
  Select, SelectContent, SelectGroup,
  SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import WorkflowCreationDialog from '@/components/automations-screen/WorkflowCreationDialog.vue';
import Button from '@/components/ui/button/Button.vue';
import { useRouter } from 'vue-router';
import InfoDropdown from '@/components/automations-screen/InfoDropdown.vue';
import { useStore } from 'vuex';
import { createNewWorkflow, getWorkflowById, getWorkflows, resetWorkflows, Workflow, WorkflowCreationRequest } from '@/http/workflow';
import Toaster from '@/components/ui/toast/Toaster.vue';
import { toast } from '@/components/ui/toast';
import { CircleX, RefreshCw } from 'lucide-vue-next';
import DialogTrigger from '@/components/ui/dialog/DialogTrigger.vue';
import DialogContent from '@/components/ui/dialog/DialogContent.vue';
import DialogHeader from '@/components/ui/dialog/DialogHeader.vue';
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue';
import DialogClose from '@/components/ui/dialog/DialogClose.vue';
import DialogFooter from '@/components/ui/dialog/DialogFooter.vue';
import Dialog from '@/components/ui/dialog/Dialog.vue';
import { set } from '@vueuse/core';
import SelectLabel from '@/components/ui/select/SelectLabel.vue';
import { predefinedWorkflows } from '@/lib/workflow';


export default /*#__PURE__*/_defineComponent({
  __name: 'AutomationsView',
  setup(__props) {

const store = useStore();
const router = useRouter();
const currentPath = computed(() => router.currentRoute.value.path);
const openReset = ref(false);

const workflows = ref<Workflow[] | undefined>(undefined);

onMounted(async () => {
  const partnerId = localStorage.getItem('PARTNER_ID');
  const credentials = localStorage.getItem('CREDENTIALS');

  if (partnerId === null) {
    throw new Error('PartnerId is null');
  }

  if (credentials === null) {
    throw new Error('Credentials is null');
  }

  workflows.value = await getWorkflows(partnerId, credentials);
  tryToRedirectToExistingWorkflow(currentPath.value);
});

const pushNewWorkflowIntoList = (workflow: Workflow) => {
  workflows.value?.push(workflow);
};

const refreshWorkflowOnList = async (workflowId: string) => {
  const credentials = store.getters[GET_CREDENTIALS];
  const workflowFromAPI = await getWorkflowById(workflowId, credentials);
  workflows.value = workflows.value?.map(workflow => workflow.id === workflowId
    ? workflowFromAPI
    : workflow);
  selectedWorkflow.value = workflowFromAPI;
};

router.afterEach((to) => {
  if (to.fullPath === '/dashboard/automacoes') {
    set(selectedWorkflow, undefined);
    set(selectedWorkflowCode, undefined);
  }
});

const selectedWorkflowCode = ref<string | undefined>(undefined);
watch(selectedWorkflowCode, () => {
  const found = workflows.value?.filter(workflow => workflow.workflowCode === selectedWorkflowCode.value);
  selectedWorkflow.value = found?.at(0) ?? undefined;
});

const selectedWorkflow = ref<Workflow | undefined>(undefined);
watch(selectedWorkflow, () => {
  redirectToWorkflowRoute(selectedWorkflow.value?.workflowCode);
});

watch(currentPath, (newPath) => {
  tryToRedirectToExistingWorkflow(newPath);
});

const tryToRedirectToExistingWorkflow = (newPath: string) => {
  if (workflows.value === undefined) return;

  const newPathParts = newPath.split("/");
  if (newPathParts.length !== 4) return;

  const workflowCode = newPathParts.at(-1)?.replace("%23", "#");
  const workflowCodeExists = workflows.value.find(
    workflow => workflow.workflowCode === workflowCode
  ) != undefined;

  if (!workflowCodeExists) {
    toast({
      title: "Este fluxo não existe!",
      variant: "destructive"
    });
    return;
  }
  selectedWorkflowCode.value = workflowCode;
};

const redirectToWorkflowRoute = async (workflowCode: string | undefined) => {
  if (workflowCode === undefined) return;
  const encodedWorkflowCode = encodeURIComponent(workflowCode);
  const uri = `/dashboard/automacoes/${encodedWorkflowCode}`;
  await router.push({ path: uri });
};

const isModalCreationOpen = ref(false);
const isWorkflowDataValid = ref(true);
const validationErrorMessage = ref("");

const tryToSaveWorkflow = async (workflowData: WorkflowCreationRequest, callback: CallableFunction) => {

  if (!validateWorkflowData(workflowData)) {
    isWorkflowDataValid.value = false;
    return;
  }

  isWorkflowDataValid.value = true;

  const partnerId = store.getters[GET_PARTNER_ID];
  const credentials = store.getters[GET_CREDENTIALS];

  workflowData.partnerId = partnerId;

  const createdWorkflow = await createNewWorkflow(workflowData, credentials);

  isModalCreationOpen.value = false;
  selectedWorkflowCode.value = createdWorkflow.workflowCode;

  callback();
  pushNewWorkflowIntoList(createdWorkflow);
  redirectToWorkflowRoute(createdWorkflow.workflowCode);
};

const validateWorkflowData = (workflowData: WorkflowCreationRequest) => {
  if (workflowData.presentationName === '') {
    validationErrorMessage.value = 'O nome não pode ser vazio!';
    return false;
  }

  if (workflows.value === undefined) return;

  const workflowExists = workflows.value.filter(workflow => workflow.presentationName === workflowData.presentationName).length > 0;
  if (workflowExists) {
    validationErrorMessage.value = 'Este nome já existe!';
    return false;
  }

  return true;
};

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(HeaderComponent, {
        title: "Automações",
        class: "flex-1 flex-grow-0"
      }, {
        default: _withCtx(() => [
          _createVNode(InfoDropdown, {
            selectedWorkflow: selectedWorkflow.value,
            "onUpdate:selectedWorkflow": _cache[0] || (_cache[0] = ($event: any) => ((selectedWorkflow).value = $event)),
            onRefreshWorkflowOnList: refreshWorkflowOnList
          }, null, 8, ["selectedWorkflow"]),
          _createElementVNode("div", _hoisted_2, [
            (selectedWorkflow.value == undefined)
              ? (_openBlock(), _createBlock(Dialog, {
                  key: 0,
                  open: openReset.value,
                  "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((openReset).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(DialogTrigger, { "as-child": "" }, {
                      default: _withCtx(() => [
                        _createVNode(Button, {
                          variant: "ghost",
                          size: "icon"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(RefreshCw), { class: "text-slate-400" })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(DialogContent, { class: "pt-4" }, {
                      default: _withCtx(() => [
                        _createVNode(DialogHeader, { class: "flex flex-row justify-between items-center" }, {
                          default: _withCtx(() => [
                            _createVNode(DialogTitle, null, {
                              default: _withCtx(() => [
                                _createTextVNode("Resetar todos os fluxos e passos?")
                              ]),
                              _: 1
                            }),
                            _createVNode(DialogClose, null, {
                              default: _withCtx(() => [
                                _createVNode(Button, {
                                  variant: "ghost",
                                  class: "px-1.5",
                                  onClick: _cache[1] || (_cache[1] = () => openReset.value = false)
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(CircleX), { class: "text-gray-600" })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _hoisted_3,
                        _createVNode(DialogFooter, null, {
                          default: _withCtx(() => [
                            _createVNode(Button, {
                              type: "submit",
                              onClick: _cache[2] || (_cache[2] = async () => {
                  try {
                    await _unref(resetWorkflows)(_unref(store).getters[_unref(PARTNER_ID)], _unref(store).getters[_unref(GET_CREDENTIALS)]);
                    await _unref(router).push({ path: '/dashboard/automacoes' });
                  } catch (err) {
                    _unref(toast)({
                      variant: 'destructive',
                      title: 'Erro ao redefinir seus fluxos e passos',
                      description: 'Não foi possível concluir a redefinição de fluxos e passos. Verifique a conexão, atualize a página e tente novamente.',
                      duration: 2000
                    });
                  } finally {
                    openReset.value = false;
                  }
                  _unref(toast)({
                    variant: 'default',
                    title: 'Redefinição de passos e fluxos concluída',
                    description: 'Agora seus fluxos e passos voltaram ao estado inicial do Converza',
                    duration: 2000
                  });
                  selectedWorkflowCode.value = undefined
                  selectedWorkflow.value = undefined;
                }),
                              variant: "destructive"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Resetar ")
                              ]),
                              _: 1
                            }),
                            _createVNode(Button, {
                              variant: "ghost",
                              onClick: _cache[3] || (_cache[3] = () => openReset.value = false),
                              class: "px-1.5"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode("Cancelar")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["open"]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(_unref(Select), {
            modelValue: selectedWorkflowCode.value,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((selectedWorkflowCode).value = $event))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(SelectTrigger), { class: "max-w-52 min-w-52 text-left" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SelectValue), { placeholder: "Selecione um fluxo" })
                ]),
                _: 1
              }),
              _createVNode(_unref(SelectContent), { class: "max-w-52 z-auto" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SelectGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(SelectLabel, { class: "text-gray-500" }, {
                        default: _withCtx(() => [
                          _createTextVNode("Fluxos")
                        ]),
                        _: 1
                      }),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workflows.value, (workflow) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: workflow.workflowCode
                        }, [
                          (workflow.workflowCode === '#DEFAULT')
                            ? (_openBlock(), _createBlock(_unref(SelectItem), {
                                key: 0,
                                value: workflow.workflowCode,
                                class: "max-w-52"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_4, _toDisplayString(workflow.presentationName ?? workflow.workflowCode), 1)
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128)),
                      _createVNode(WorkflowCreationDialog, {
                        isModalCreationOpen: isModalCreationOpen.value,
                        "onUpdate:isModalCreationOpen": _cache[6] || (_cache[6] = ($event: any) => ((isModalCreationOpen).value = $event)),
                        isWorkflowDataValid: isWorkflowDataValid.value,
                        "onUpdate:isWorkflowDataValid": _cache[7] || (_cache[7] = ($event: any) => ((isWorkflowDataValid).value = $event)),
                        validationErrorMessage: validationErrorMessage.value,
                        "onUpdate:validationErrorMessage": _cache[8] || (_cache[8] = ($event: any) => ((validationErrorMessage).value = $event)),
                        onTryToSaveWorkflow: tryToSaveWorkflow
                      }, {
                        default: _withCtx(() => [
                          _createVNode(Button, {
                            disabled: "",
                            size: "sm",
                            class: "flex items-center w-full mt-1 justify-center max-sm:text-sm font-semibold",
                            onClick: _cache[5] || (_cache[5] = () => isModalCreationOpen.value = true)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" Criar Fluxo ")
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["isModalCreationOpen", "isWorkflowDataValid", "validationErrorMessage"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SelectGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(SelectLabel, { class: "text-gray-500" }, {
                        default: _withCtx(() => [
                          _createTextVNode("Passos")
                        ]),
                        _: 1
                      }),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workflows.value, (workflow) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: workflow.workflowCode
                        }, [
                          (_unref(predefinedWorkflows)?.includes(workflow.workflowCode) && workflow.workflowCode !== '#DEFAULT')
                            ? (_openBlock(), _createBlock(_unref(SelectItem), {
                                key: 0,
                                value: workflow.workflowCode,
                                class: "max-w-52"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_5, _toDisplayString(workflow.presentationName ?? workflow.workflowCode), 1)
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_component_router_view, {
          currentWorkflow: selectedWorkflow.value,
          "onUpdate:currentWorkflow": _cache[10] || (_cache[10] = ($event: any) => ((selectedWorkflow).value = $event))
        }, null, 8, ["currentWorkflow"])
      ])
    ]),
    _createVNode(Toaster)
  ], 64))
}
}

})